import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Pagination,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { useSearchParams } from "react-router-dom";
import {
  assignConversation,
  getConversation,
  getConversationMessages,
  getConversations,
  markConversationRead,
  returnAllConversationsToAI,
  returnConversationToAI,
  sendConversationReply,
  takeConversation,
  unassignConversation,
  updateConversation,
} from "../api/conversations";
import { getApiError } from "../api/errors";
import { getEmployeeOperationMembers } from "../api/employeeOperations";
import { markNotificationRead } from "../api/notifications";
import { EmptyPanel, LoadingPanel } from "../components/PageState";
import { useAuth } from "../context/AuthContext";
import useDebouncedValue from "../hooks/useDebouncedValue";
import PageHeader from "../components/PageHeader";
import MainLayout from "../layouts/MainLayout";
import { createIdempotencyKey, formatDate, titleCase } from "../utils/format";

const PAGE_SIZE = 24;

function providerColor(provider) {
  if (provider === "whatsapp") return "success";
  if (provider === "instagram") return "secondary";
  return "default";
}

function automationInfo(status) {
  if (status === "handoff_requested") {
    return { label: "Needs employee", color: "warning", variant: "filled" };
  }
  if (status === "human_active") {
    return { label: "Human active", color: "info", variant: "filled" };
  }
  return { label: "AI active", color: "success", variant: "outlined" };
}

function MessageBubble({ message }) {
  const outbound = message.direction === "outbound";
  const senderLabel = message.sender_type === "system"
    ? "System"
    : outbound
      ? titleCase(message.sender_type)
      : "Customer";
  return (
    <Box sx={{ display: "flex", justifyContent: outbound ? "flex-end" : "flex-start" }}>
      <Box
        sx={{
          maxWidth: { xs: "92%", sm: "76%" },
          px: 2,
          py: 1.4,
          borderRadius: outbound ? "18px 18px 4px 18px" : "18px 18px 18px 4px",
          bgcolor: message.sender_type === "system"
            ? "#fef3c7"
            : outbound
              ? "#dbeafe"
              : "#f1f5f9",
          border: "1px solid",
          borderColor: message.sender_type === "system"
            ? "#fde68a"
            : outbound
              ? "#bfdbfe"
              : "#e2e8f0",
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
          <Typography variant="caption" fontWeight={900}>{senderLabel}</Typography>
          <Typography variant="caption" color="text.secondary">
            {formatDate(message.occurred_at)}
          </Typography>
        </Stack>
        <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
          {message.text || `[${titleCase(message.content_type)} message]`}
        </Typography>
        {message.media_reference && (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.8 }}>
            Media reference stored securely
          </Typography>
        )}
        {outbound && (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.8, textAlign: "right" }}>
            {titleCase(message.delivery_status)}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

export default function Conversations() {
  const { canEdit, canManage, selectedWorkspace, selectedStore } = useAuth();
  const [searchParams] = useSearchParams();
  const openFromNotification = searchParams.get("open") || "";
  const notificationFromPush = searchParams.get("notification") || "";
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("open");
  const [providerFilter, setProviderFilter] = useState("");
  const [automationFilter, setAutomationFilter] = useState("");
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [selectedId, setSelectedId] = useState("");
  const [detail, setDetail] = useState(null);
  const [messages, setMessages] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [replyText, setReplyText] = useState("");
  const [replying, setReplying] = useState(false);
  const messageEndRef = useRef(null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const debouncedQuery = useDebouncedValue(query.trim(), 350);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await getConversations({
        query: debouncedQuery || undefined,
        status: statusFilter || undefined,
        provider: providerFilter || undefined,
        automation_status: automationFilter || undefined,
        unread_only: unreadOnly || undefined,
        page,
        page_size: PAGE_SIZE,
      });
      const nextItems = response.data?.items || [];
      setItems(nextItems);
      setTotal(Number(response.data?.total || 0));
      if (
        selectedId &&
        !openFromNotification &&
        !nextItems.some((item) => item.public_id === selectedId)
      ) {
        setSelectedId("");
        setDetail(null);
        setMessages([]);
      }
    } catch (requestError) {
      setError(getApiError(requestError, "Could not load conversations."));
    } finally {
      setLoading(false);
    }
  }, [
    automationFilter,
    debouncedQuery,
    openFromNotification,
    page,
    providerFilter,
    selectedId,
    statusFilter,
    unreadOnly,
  ]);

  useEffect(() => {
    load();
  }, [load, selectedWorkspace?.public_id, selectedStore?.public_id]);

  useEffect(() => {
    if (!canEdit) {
      setEmployees([]);
      return;
    }
    getEmployeeOperationMembers()
      .then((response) => setEmployees(response.data?.items || []))
      .catch(() => setEmployees([]));
  }, [canEdit, selectedWorkspace?.public_id]);

  useEffect(() => {
    const timer = window.setInterval(() => load(), 15000);
    return () => window.clearInterval(timer);
  }, [load]);

  useEffect(() => {
    setPage(1);
  }, [automationFilter, debouncedQuery, providerFilter, statusFilter, unreadOnly]);

  const openConversation = useCallback(async (conversationPublicId) => {
    setSelectedId(conversationPublicId);
    setDetailLoading(true);
    setError("");
    try {
      const [detailResponse, messageResponse] = await Promise.all([
        getConversation(conversationPublicId),
        getConversationMessages(conversationPublicId, { page: 1, page_size: 200 }),
      ]);
      setDetail(detailResponse.data);
      setMessages(messageResponse.data?.items || []);
      if (canEdit && Number(detailResponse.data?.unread_count || 0) > 0) {
        const readResponse = await markConversationRead(conversationPublicId);
        setDetail((current) => ({ ...current, ...readResponse.data }));
        setItems((current) => current.map((item) =>
          item.public_id === conversationPublicId
            ? { ...item, unread_count: 0 }
            : item,
        ));
      }
    } catch (requestError) {
      setError(getApiError(requestError, "Could not open the conversation."));
    } finally {
      setDetailLoading(false);
    }
  }, [canEdit]);

  useEffect(() => {
    if (openFromNotification && openFromNotification !== selectedId) {
      openConversation(openFromNotification);
    }
  }, [openConversation, openFromNotification, selectedId]);

  useEffect(() => {
    if (!notificationFromPush) return;
    markNotificationRead(notificationFromPush).catch(() => {
      // The conversation remains accessible even if read-state persistence fails.
    });
  }, [notificationFromPush]);

  const applyUpdate = (updated) => {
    setDetail((current) => ({ ...current, ...updated }));
    setItems((current) => current.map((item) =>
      item.public_id === updated.public_id ? { ...item, ...updated } : item,
    ));
  };

  const toggleStatus = async () => {
    if (!detail) return;
    const nextStatus = detail.status === "open" ? "closed" : "open";
    setSaving(true);
    setError("");
    try {
      const response = await updateConversation(detail.public_id, { status: nextStatus });
      applyUpdate(response.data);
      if (statusFilter && statusFilter !== nextStatus) {
        setSelectedId("");
        setDetail(null);
        setMessages([]);
        await load();
      }
    } catch (requestError) {
      setError(getApiError(requestError, "Could not update the conversation."));
    } finally {
      setSaving(false);
    }
  };

  const toggleAI = async () => {
    if (!detail || detail.provider !== "whatsapp") return;
    setSaving(true);
    setError("");
    try {
      const response = await updateConversation(detail.public_id, {
        ai_enabled: !detail.ai_enabled,
      });
      applyUpdate(response.data);
    } catch (requestError) {
      setError(getApiError(requestError, "Could not update AI replies for this chat."));
    } finally {
      setSaving(false);
    }
  };

  const takeSelected = async () => {
    if (!detail) return;
    setSaving(true);
    setError("");
    try {
      const response = await takeConversation(detail.public_id);
      applyUpdate(response.data);
      await load();
    } catch (requestError) {
      setError(getApiError(requestError, "Could not take over the conversation."));
    } finally {
      setSaving(false);
    }
  };

  const assignSelected = async (userPublicId) => {
    if (!detail || !userPublicId) return;
    setSaving(true);
    setError("");
    try {
      const response = await assignConversation(detail.public_id, userPublicId);
      applyUpdate(response.data);
      await load();
    } catch (requestError) {
      setError(getApiError(requestError, "Could not assign the conversation."));
    } finally {
      setSaving(false);
    }
  };

  const unassignSelected = async () => {
    if (!detail) return;
    setSaving(true);
    setError("");
    try {
      const response = await unassignConversation(detail.public_id);
      applyUpdate(response.data);
      await load();
    } catch (requestError) {
      setError(getApiError(requestError, "Could not return the conversation to the employee queue."));
    } finally {
      setSaving(false);
    }
  };

  const returnAllToAI = async () => {
    const confirmed = window.confirm(
      "Return every open WhatsApp conversation in this store to AI? " +
      "This will remove employee assignments and close pending handoffs."
    );
    if (!confirmed || saving) return;

    setSaving(true);
    setError("");
    setSuccess("");
    try {
      const response = await returnAllConversationsToAI();
      const returnedCount = Number(response.data?.returned_count || 0);
      setSuccess(
        returnedCount === 0
          ? "All eligible conversations are already assigned to AI."
          : `${returnedCount} conversation${returnedCount === 1 ? "" : "s"} returned to AI.`
      );
      if (detail?.provider === "whatsapp" && detail?.status === "open" && detail?.ai_enabled) {
        await openConversation(detail.public_id);
      }
      await load();
    } catch (requestError) {
      setError(getApiError(requestError, "Could not return all conversations to AI."));
    } finally {
      setSaving(false);
    }
  };

  const returnToAI = async () => {
    if (!detail) return;
    setSaving(true);
    setError("");
    try {
      const response = await returnConversationToAI(detail.public_id);
      applyUpdate(response.data);
      await load();
    } catch (requestError) {
      setError(getApiError(requestError, "Could not return the conversation to AI."));
    } finally {
      setSaving(false);
    }
  };

  const sendReply = async () => {
    const text = replyText.trim();
    if (!detail || !text || replying) return;
    setReplying(true);
    setError("");
    try {
      await sendConversationReply(
        detail.public_id,
        text,
        createIdempotencyKey("whatsapp-reply"),
      );
      setReplyText("");
      await openConversation(detail.public_id);
      await load();
    } catch (requestError) {
      setError(getApiError(requestError, "Could not queue the WhatsApp reply."));
    } finally {
      setReplying(false);
    }
  };

  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const unreadTotal = useMemo(
    () => items.reduce((sum, item) => sum + Number(item.unread_count || 0), 0),
    [items],
  );
  const waitingTotal = useMemo(
    () => items.filter((item) => item.automation_status === "handoff_requested").length,
    [items],
  );

  const detailAutomation = automationInfo(detail?.automation_status);

  useEffect(() => {
    if (detail?.public_id && messages.length) {
      messageEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
    }
  }, [detail?.public_id, messages.length]);

  return (
    <MainLayout>
      <PageHeader
        title="Conversations"
        description="AI and employees share one durable WhatsApp conversation safely."
        actions={(
          <>
            <Chip label={`${total.toLocaleString()} conversations`} sx={{ fontWeight: 800 }} />
            <Chip
              label={`${waitingTotal} waiting on this page`}
              color={waitingTotal ? "warning" : "default"}
              sx={{ fontWeight: 800 }}
            />
            <Chip
              label={`${unreadTotal} unread on this page`}
              color={unreadTotal ? "warning" : "default"}
              variant="outlined"
              sx={{ fontWeight: 800 }}
            />
            {canManage && (
              <Button
                variant="contained"
                startIcon={<AutoAwesomeRoundedIcon />}
                onClick={returnAllToAI}
                disabled={saving}
              >
                Return all to AI
              </Button>
            )}
            <Button
              variant="outlined"
              startIcon={<RefreshRoundedIcon />}
              onClick={load}
              disabled={loading || saving}
            >
              Refresh
            </Button>
          </>
        )}
      />

      <Alert severity="info" sx={{ mb: 3, borderRadius: 3 }}>
        The AI identifies itself in its first reply. When a customer asks for an employee, AI stops and the conversation moves to Needs employee.
      </Alert>
      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 3 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 3, borderRadius: 3 }} onClose={() => setSuccess("")}>
          {success}
        </Alert>
      )}

      <Stack direction={{ xs: "column", lg: "row" }} spacing={2} sx={{ mb: 3 }}>
        <TextField
          fullWidth
          label="Search conversations"
          placeholder="Customer, phone, message preview, or channel"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          sx={{ bgcolor: "white", maxWidth: 600 }}
        />
        <FormControl sx={{ minWidth: 150, bgcolor: "white" }}>
          <InputLabel>Status</InputLabel>
          <Select value={statusFilter} label="Status" onChange={(event) => setStatusFilter(event.target.value)}>
            <MenuItem value="">All</MenuItem>
            <MenuItem value="open">Open</MenuItem>
            <MenuItem value="closed">Closed</MenuItem>
          </Select>
        </FormControl>
        <FormControl sx={{ minWidth: 170, bgcolor: "white" }}>
          <InputLabel>Automation</InputLabel>
          <Select value={automationFilter} label="Automation" onChange={(event) => setAutomationFilter(event.target.value)}>
            <MenuItem value="">All</MenuItem>
            <MenuItem value="ai_active">AI active</MenuItem>
            <MenuItem value="handoff_requested">Needs employee</MenuItem>
            <MenuItem value="human_active">Human active</MenuItem>
          </Select>
        </FormControl>
        <FormControl sx={{ minWidth: 160, bgcolor: "white" }}>
          <InputLabel>Channel</InputLabel>
          <Select value={providerFilter} label="Channel" onChange={(event) => setProviderFilter(event.target.value)}>
            <MenuItem value="">All</MenuItem>
            <MenuItem value="whatsapp">WhatsApp</MenuItem>
            <MenuItem value="instagram">Instagram</MenuItem>
          </Select>
        </FormControl>
        <Stack direction="row" alignItems="center" sx={{ px: 1 }}>
          <Switch checked={unreadOnly} onChange={(event) => setUnreadOnly(event.target.checked)} />
          <Typography variant="body2" fontWeight={800}>Unread only</Typography>
        </Stack>
      </Stack>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "minmax(330px, 420px) 1fr" }, gap: 2.5, alignItems: "start" }}>
        <Box sx={{ display: { xs: selectedId ? "none" : "block", lg: "block" } }}>
          {loading ? (
            <LoadingPanel label="Loading conversations…" />
          ) : items.length === 0 ? (
            <EmptyPanel icon="💬" title="No conversations found" description="New channel conversations appear here automatically." />
          ) : (
            <Stack spacing={1.25}>
              {items.map((item) => {
                const automation = automationInfo(item.automation_status);
                return (
                  <Card
                    key={item.public_id}
                    sx={{
                      borderRadius: 3,
                      border: "1px solid",
                      borderColor: item.automation_status === "handoff_requested"
                        ? "warning.main"
                        : selectedId === item.public_id
                          ? "primary.main"
                          : "#e2e8f0",
                      boxShadow: selectedId === item.public_id ? "0 10px 28px rgba(37,99,235,.12)" : "none",
                    }}
                  >
                    <CardActionArea onClick={() => openConversation(item.public_id)}>
                      <CardContent sx={{ p: 2 }}>
                        <Stack direction="row" justifyContent="space-between" gap={2}>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography fontWeight={900} noWrap>{item.customer.display_name}</Typography>
                            <Typography variant="caption" color="text.secondary" noWrap display="block">
                              {item.customer.phone || item.external_conversation_id}
                            </Typography>
                          </Box>
                          <Stack alignItems="flex-end" spacing={0.5}>
                            <Chip label={titleCase(item.provider)} size="small" color={providerColor(item.provider)} variant="outlined" sx={{ fontWeight: 800 }} />
                            {item.provider === "whatsapp" && (
                              <Chip label={automation.label} size="small" color={automation.color} variant={automation.variant} sx={{ fontWeight: 800 }} />
                            )}
                            {item.unread_count > 0 && <Chip label={item.unread_count} size="small" color="warning" />}
                          </Stack>
                        </Stack>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1.2 }} noWrap>
                          {item.last_message_preview || "No message preview"}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                          {item.last_message_at ? formatDate(item.last_message_at) : "No messages"}
                        </Typography>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                );
              })}
            </Stack>
          )}
          {pageCount > 1 && (
            <Stack alignItems="center" sx={{ mt: 3 }}>
              <Pagination page={page} count={pageCount} onChange={(_, value) => setPage(value)} shape="rounded" color="primary" />
            </Stack>
          )}
        </Box>

        <Card sx={{ display: { xs: selectedId ? "block" : "none", lg: "block" }, borderRadius: 4, border: "1px solid #e2e8f0", minHeight: 520, overflow: "hidden" }}>
          {detailLoading ? (
            <LoadingPanel label="Loading conversation history…" />
          ) : !detail ? (
            <EmptyPanel icon="📨" title="Select a conversation" description="Open a customer conversation to review its complete history." />
          ) : (
            <>
              <CardContent sx={{ p: { xs: 2.2, sm: 3 }, position: "sticky", top: 0, zIndex: 2, bgcolor: "rgba(255,255,255,.96)", backdropFilter: "blur(12px)" }}>
                <Button startIcon={<ArrowBackRoundedIcon />} onClick={() => { setSelectedId(""); setDetail(null); setMessages([]); }} sx={{ display: { xs: "inline-flex", lg: "none" }, mb: 1.5 }}>Back to conversations</Button>
                <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" gap={2}>
                  <Box>
                    <Typography variant="h5" fontWeight={900}>{detail.customer.display_name}</Typography>
                    <Typography color="text.secondary">
                      {detail.customer.phone || detail.customer.email || "No contact details"}
                    </Typography>
                    <Stack direction="row" spacing={1} sx={{ mt: 1.2 }} flexWrap="wrap" useFlexGap>
                      <Chip label={titleCase(detail.provider)} color={providerColor(detail.provider)} size="small" />
                      <Chip label={titleCase(detail.status)} size="small" variant="outlined" />
                      <Chip label={detailAutomation.label} size="small" color={detailAutomation.color} variant={detailAutomation.variant} />
                      <Chip label={`${detail.message_count} messages`} size="small" variant="outlined" />
                      {detail.assigned_user_name && (
                        <Chip label={`Assigned: ${detail.assigned_user_name}`} size="small" color="info" variant="outlined" />
                      )}
                    </Stack>
                  </Box>
                  {canEdit && (
                    <Stack spacing={1.2} alignItems={{ xs: "flex-start", sm: "flex-end" }}>
                      {detail.provider === "whatsapp" && (
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Typography variant="body2" fontWeight={900}>AI allowed {detail.ai_enabled ? "ON" : "OFF"}</Typography>
                          <Switch checked={Boolean(detail.ai_enabled)} onChange={toggleAI} disabled={saving} />
                        </Stack>
                      )}
                      {canManage && detail.provider === "whatsapp" && detail.status === "open" && (
                        <FormControl size="small" sx={{ minWidth: 190 }}>
                          <InputLabel>Assign employee</InputLabel>
                          <Select
                            label="Assign employee"
                            value={detail.assigned_user_public_id || ""}
                            onChange={(event) => assignSelected(event.target.value)}
                            disabled={saving}
                          >
                            {employees.map((employee) => (
                              <MenuItem key={employee.user_public_id} value={employee.user_public_id}>
                                {employee.username} · {titleCase(employee.availability_status)}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      )}
                      {canManage && detail.assigned_user_public_id && detail.status === "open" && (
                        <Button color="inherit" onClick={unassignSelected} disabled={saving}>
                          Unassign
                        </Button>
                      )}
                      {detail.provider === "whatsapp" && detail.status === "open" && (
                        detail.automation_status === "human_active" ? (
                          <Button variant="contained" color="success" onClick={returnToAI} disabled={saving || !detail.ai_enabled}>
                            Return to AI
                          </Button>
                        ) : (
                          <Button variant="contained" color="warning" onClick={takeSelected} disabled={saving}>
                            Take conversation
                          </Button>
                        )
                      )}
                      <Button variant="outlined" onClick={toggleStatus} disabled={saving}>
                        {detail.status === "open" ? "Close conversation" : "Reopen conversation"}
                      </Button>
                    </Stack>
                  )}
                </Stack>

                {detail.automation_status === "handoff_requested" && (
                  <Alert severity="warning" sx={{ mt: 2, borderRadius: 2 }}>
                    The customer requested an employee. AI replies are paused until an employee takes the conversation or returns it to AI.
                  </Alert>
                )}
                {detail.automation_status === "human_active" && (
                  <Alert severity="info" sx={{ mt: 2, borderRadius: 2 }}>
                    A human owns this conversation. AI will remain silent.
                  </Alert>
                )}
                {detail.ai_disclosure_sent_at && (
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1.2 }}>
                    AI identity disclosure issued {formatDate(detail.ai_disclosure_sent_at)}.
                  </Typography>
                )}
              </CardContent>
              <Divider />
              <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: "#fff", minHeight: 380, maxHeight: "65vh", overflowY: "auto" }}>
                {messages.length === 0 ? (
                  <Typography color="text.secondary">No stored messages.</Typography>
                ) : (
                  <Stack spacing={1.4}>{messages.map((message) => <MessageBubble key={message.public_id} message={message} />)}<Box ref={messageEndRef} /></Stack>
                )}
              </Box>

              {canEdit && detail.provider === "whatsapp" && detail.status === "open" && (
                <>
                  <Divider />
                  <Box sx={{ p: { xs: 2, sm: 2.5 }, bgcolor: "#f8fafc" }}>
                    {detail.automation_status === "ai_active" && (
                      <Alert severity="warning" sx={{ mb: 1.5, borderRadius: 2 }}>
                        Sending a manual reply automatically switches this conversation to Human active to prevent duplicate AI replies.
                      </Alert>
                    )}
                    {!detail.ai_enabled && (
                      <Alert severity="info" sx={{ mb: 1.5, borderRadius: 2 }}>
                        AI is disabled for this chat. Manual replies still work normally.
                      </Alert>
                    )}
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={1.2} alignItems="stretch">
                      <TextField
                        fullWidth
                        multiline
                        maxRows={5}
                        label="Reply on WhatsApp"
                        value={replyText}
                        onChange={(event) => setReplyText(event.target.value)}
                        onKeyDown={(event) => {
                          if (event.key === "Enter" && !event.shiftKey) {
                            event.preventDefault();
                            sendReply();
                          }
                        }}
                        disabled={replying}
                        inputProps={{ maxLength: 4000 }}
                      />
                      <Button variant="contained" startIcon={<SendRoundedIcon />} onClick={sendReply} disabled={replying || !replyText.trim()} sx={{ minWidth: 120 }}>{replying ? "Queuing…" : "Send"}</Button>
                    </Stack>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.8 }}>
                      Enter sends. Shift+Enter adds a new line.
                    </Typography>
                  </Box>
                </>
              )}
            </>
          )}
        </Card>
      </Box>
    </MainLayout>
  );
}
